import { useOptimizedPersonProfiles, PersonProfile, CreatePersonWithRelationData, CreatePersonData } from './useOptimizedPersonProfiles';
import { useFamily } from '@/hooks/useFamily';

/**
 * Wrapper around useOptimizedPersonProfiles to maintain compatibility with legacy usePersonProfiles interface
 * This allows existing components to gradually migrate to the optimized version
 */
export function usePersonProfiles(familyId?: string) {
  const { family } = useFamily();
  const effectiveFamilyId = familyId || family?.id;

  const {
    persons,
    loading,
    createPerson: optimizedCreatePerson,
    updatePerson: optimizedUpdatePerson,
    deletePerson: optimizedDeletePerson,
    refreshPersons,
    getPersonsByCategory,
    getPersonsByFamilyRole,
    getFamilyMembers,
    getParents,
    getChildren,
    getEmergencyContacts,
    searchPersons,
  } = useOptimizedPersonProfiles(effectiveFamilyId);

  // Convert optimized functions to match legacy interface
  const createPerson = async (personData: CreatePersonWithRelationData) => {
    return optimizedCreatePerson(personData);
  };

  const updatePerson = async (personId: string, personData: Partial<CreatePersonData>) => {
    return optimizedUpdatePerson(personId, personData);
  };

  const deletePerson = async (personId: string) => {
    return optimizedDeletePerson(personId);
  };

  return {
    persons,
    loading,
    createPerson,
    updatePerson,
    deletePerson,
    refreshPersons,
    getPersonsByCategory,
    getPersonsByFamilyRole,
    getFamilyMembers,
    getParents,
    getChildren,
    getEmergencyContacts,
    searchPersons,
  };
}

// Re-export the types for compatibility
export type { PersonProfile, CreatePersonWithRelationData, CreatePersonData };